<template>
    <v-container fluid>
        <v-card>
            <v-card-title>
                <v-row dense align="center">
                    <v-col cols="12" md="7">
                        <div class="text-subtitle-1 font-weight-bold">
                            <v-icon left color="primary">design_services</v-icon>Plantillas de constancias
                        </div>
                        <div class="text-caption grey--text text--darken-1">
                            Documento Word (.docx) con el que se genera cada constancia. Puede haber una plantilla por unidad
                            operativa y una general; la firma del jefe de departamento sale de la unidad operativa de la importación.
                        </div>
                    </v-col>
                    <v-col cols="12" md="5" class="text-md-right">
                        <v-btn color="info" small outlined class="mr-2" :loading="cargando" @click="cargar"><v-icon small>refresh</v-icon></v-btn>
                        <v-btn color="success" small outlined v-if="permisos.crear" @click="nueva"><v-icon small left>add</v-icon> Nueva plantilla</v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-alert dense text type="info" class="mb-4">
                    <div class="d-flex flex-wrap align-center" style="gap: 8px 16px">
                        <span>Para personalizar, descargue la plantilla base, edítela en Word conservando los marcadores y súbala aquí:</span>
                        <a v-for="b in baseDisponible" :key="b.tipo" :href="b.url" target="_blank" rel="noopener">
                            <v-icon small color="primary">download</v-icon> {{ b.texto }}
                        </a>
                        <v-btn x-small text color="primary" @click="verMarcadores = !verMarcadores">
                            {{ verMarcadores ? 'Ocultar marcadores' : 'Ver marcadores' }}
                        </v-btn>
                    </div>
                    <div v-if="verMarcadores" class="mt-2 text-caption">
                        <b>Texto:</b> <code v-for="m in marcadores.texto" :key="m" class="mr-1">${{ '{' + m + '}' }}</code>
                        (para más cursos use <code>nombre_curso_2 … nombre_curso_6</code>, <code>horas_2</code>, <code>nota_2</code>, <code>f_ini_2</code>, <code>f_fin_2</code>).<br>
                        <b>Imágenes</b> (un cuadro de imagen cuyo texto alternativo sea el marcador):
                        <code v-for="m in marcadores.imagen" :key="m" class="mr-1">${{ '{' + m + '}' }}</code>.
                        El código QR se imprime al 65 % del tamaño de su cuadro.
                    </div>
                </v-alert>

                <v-data-table
                    dense
                    :headers="encabezados"
                    :items="plantillas"
                    :loading="cargando"
                    item-key="plantilla_constancia_id"
                    :items-per-page="25"
                    :footer-props="$store.state.footerPropsTable"
                >
                    <template v-slot:[`item.unidad_operativa`]="{ item }">
                        <span v-if="item.unidad_operativa">{{ item.unidad_operativa }}</span>
                        <v-chip v-else x-small label color="primary" outlined>General</v-chip>
                    </template>
                    <template v-slot:[`item.archivo_nombre`]="{ item }">
                        <a v-if="item.url" :href="item.url" target="_blank" rel="noopener"><v-icon small>download</v-icon> {{ item.archivo_nombre || 'Descargar' }}</a>
                        <span v-else class="error--text text-caption">Archivo no encontrado</span>
                    </template>
                    <template v-slot:[`item.fecha_crea`]="{ item }">
                        {{ (item.fecha_actualiza || item.fecha_crea) | fechaESPHora }}
                        <div class="text-caption grey--text" v-if="item.registrado_por">por {{ item.registrado_por }}</div>
                    </template>
                    <template v-slot:[`item.activo`]="{ item }">
                        <v-chip x-small label :color="item.activo ? 'success' : 'grey'" text-color="white">{{ item.activo ? 'Activa' : 'Inactiva' }}</v-chip>
                    </template>
                    <template v-slot:[`item.acciones`]="{ item }">
                        <v-btn icon small color="info" v-if="permisos.editar" title="Editar" @click="editar(item)"><v-icon small>edit</v-icon></v-btn>
                        <v-btn icon small color="error" v-if="permisos.eliminar" title="Eliminar" @click="confirmarEliminar(item)"><v-icon small>delete</v-icon></v-btn>
                    </template>
                    <template v-slot:no-data>
                        <v-alert dense type="info" outlined class="text-center mt-3">
                            No hay plantillas personalizadas: las constancias se generan con la plantilla base del sistema.
                        </v-alert>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="dialogo" max-width="640" persistent scrollable>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    {{ modelo.plantilla_constancia_id ? 'Editar' : 'Nueva' }} plantilla
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="dialogo = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="form" v-model="valido" @submit.prevent="guardar">
                        <v-text-field v-model="modelo.nombre" label="Nombre" outlined dense maxlength="150" :rules="[v => !!(v && v.trim()) || 'Campo requerido']"
                                      placeholder="Ej. Constancia Formación a Distancia"/>
                        <v-select v-model="modelo.tipo" :items="tipos" item-text="texto" item-value="valor" label="Tipo de constancia" outlined dense
                                  :rules="[v => !!v || 'Campo requerido']"/>
                        <v-autocomplete v-model="modelo.unidad_operativa_id" :items="unidades" item-text="unidad_operativa" item-value="unidad_operativa_id"
                                        label="Unidad operativa" outlined dense clearable
                                        hint="Vacío = plantilla general para las unidades que no tengan una propia. De la unidad sale la firma del jefe de departamento."
                                        persistent-hint class="mb-3"/>
                        <v-file-input v-model="archivo" outlined dense accept=".docx" show-size truncate-length="40"
                                      :label="modelo.plantilla_constancia_id ? 'Reemplazar archivo (.docx)' : 'Archivo de la plantilla (.docx)'"
                                      :rules="[v => !!modelo.plantilla_constancia_id || !!v || 'Seleccione el archivo .docx']"
                                      prepend-icon="description" hint="Máximo 10 MB." persistent-hint/>
                        <v-switch v-model="modelo.activo" inset hide-details color="success" class="mt-2"
                                  :label="modelo.activo ? 'Activa: se usa al generar constancias' : 'Inactiva: se conserva pero no se usa'"/>
                    </v-form>
                    <v-alert dense text type="success" class="mt-3 mb-0" v-if="analisis">
                        Marcadores encontrados: {{ analisis.encontrados.length }}.
                    </v-alert>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogo = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="guardando" @click="guardar"><v-icon small left>save</v-icon> Guardar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogoEliminar" max-width="420">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Eliminar plantilla</v-card-title>
                <v-card-text>¿Eliminar <b>{{ aEliminar ? aEliminar.nombre : '' }}</b>? Las constancias de esa unidad volverán a usar la plantilla general o la base.</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogoEliminar = false">Cancelar</v-btn>
                    <v-btn color="error" depressed :loading="guardando" @click="eliminar">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import Auth from "@/services/auth";

export default {
    name: "AdministracionConstanciasPlantillas",
    data() {
        return {
            cargando: false,
            guardando: false,
            plantillas: [],
            tipos: [],
            unidades: [],
            base: [],
            marcadores: {texto: [], imagen: []},
            verMarcadores: false,
            permisos: {crear: false, editar: false, eliminar: false},
            dialogo: false,
            dialogoEliminar: false,
            valido: true,
            archivo: null,
            analisis: null,
            modelo: {activo: true},
            aEliminar: null,
            encabezados: [
                {text: "Nombre", value: "nombre"},
                {text: "Tipo", value: "tipo_texto"},
                {text: "Unidad operativa", value: "unidad_operativa"},
                {text: "Archivo", value: "archivo_nombre", sortable: false},
                {text: "Actualizada", value: "fecha_crea"},
                {text: "Estado", value: "activo", sortable: false},
                {text: "", value: "acciones", sortable: false, align: "end"},
            ],
        }
    },
    computed: {
        baseDisponible() {
            return this.base.filter(b => b.url);
        },
    },
    methods: {
        cargar() {
            this.cargando = true;
            window.axios.get('/plantillas_constancias').then(response => {
                if (response.data.success) {
                    const d = response.data.data;
                    this.plantillas = d.plantillas;
                    this.tipos = d.tipos;
                    this.unidades = d.unidades;
                    this.base = d.base;
                    this.marcadores = d.marcadores;
                    this.permisos = d.permisos;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudieron cargar las plantillas."))
                .finally(() => {
                    this.cargando = false;
                });
        },
        nueva() {
            this.modelo = {plantilla_constancia_id: null, nombre: "", tipo: "finalizados", unidad_operativa_id: null, activo: true};
            this.archivo = null;
            this.analisis = null;
            this.dialogo = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        editar(fila) {
            this.modelo = {plantilla_constancia_id: fila.plantilla_constancia_id, nombre: fila.nombre, tipo: fila.tipo, unidad_operativa_id: fila.unidad_operativa_id, activo: !!fila.activo};
            this.archivo = null;
            this.analisis = null;
            this.dialogo = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },
        guardar() {
            if (!this.$refs.form.validate()) return;

            this.guardando = true;
            const formData = new FormData();
            formData.append("plantilla_constancia_id", this.modelo.plantilla_constancia_id || "");
            formData.append("nombre", this.modelo.nombre.trim());
            formData.append("tipo", this.modelo.tipo);
            if (this.modelo.unidad_operativa_id) formData.append("unidad_operativa_id", this.modelo.unidad_operativa_id);
            formData.append("activo", this.modelo.activo ? 1 : 0);
            if (this.archivo) formData.append("archivo", this.archivo);

            window.axios.post('/plantillas_constancias/guardar', formData).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.dialogo = false;
                    this.cargar();
                } else {
                    this.$toast.error(response.data.message, {timeout: 12000});
                }
            })
                .catch(error => {
                    const cuerpo = error.response && error.response.data;
                    this.$toast.error((cuerpo && cuerpo.message) || "No se pudo guardar.", {timeout: 12000});
                })
                .finally(() => {
                    this.guardando = false;
                });
        },
        confirmarEliminar(fila) {
            this.aEliminar = fila;
            this.dialogoEliminar = true;
        },
        eliminar() {
            this.guardando = true;
            window.axios.post('/plantillas_constancias/eliminar', {plantilla_constancia_id: this.aEliminar.plantilla_constancia_id}).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);
                    this.dialogoEliminar = false;
                    this.cargar();
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudo eliminar."))
                .finally(() => {
                    this.guardando = false;
                });
        },
    },
    created() {
        Auth.checkPermisos("/administracion/constancias/plantillas").then(() => this.cargar());
    },
}
</script>
